import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
  ThirdPartyVendorDiary,
  Vendor,
  VendorCreateAndUpdateRequest,
} from '../../api/models/vendor-list-detail-models';
import { filterObjects } from '../../helpers';
import { RootState } from '../store';

const vendorListAdapter = createEntityAdapter<Vendor>({
  selectId: (header: Vendor) => header.ThirdPartyVendorId.toString(),
  sortComparer: (a, b) => a.VendorName.localeCompare(b.VendorName),
});
const thirdPartyVendorDiaryAdapter = createEntityAdapter<ThirdPartyVendorDiary>({
  selectId: (data) => data.ThirdPartyVendorDiaryId,
  sortComparer: (a, b) => new Date(b.ChangeDate).getTime() - new Date(a.ChangeDate).getTime(),
});

// State
interface VendorManagementState {
  isLoadingVendorList: boolean;
  vendorList: Vendor[];
  isCreateOrUpdateVendorLoading: boolean;
  isLoadingVendor: boolean;
  vendor: Vendor | undefined;
  currentVendor: VendorCreateAndUpdateRequest | undefined | Vendor;
  isDeleteVendorLoading: boolean;
  vendorListHeaders: EntityState<Vendor>;
  isThirdPartyVendorDiariesLoading: boolean;
  thirdPartyVendorDiaries: EntityState<ThirdPartyVendorDiary>;
}

const initialState: VendorManagementState = {
  isLoadingVendorList: true,
  vendorList: [],
  isCreateOrUpdateVendorLoading: false,
  isLoadingVendor: true,
  vendor: undefined,
  currentVendor: undefined,
  isDeleteVendorLoading: false,
  vendorListHeaders: vendorListAdapter.getInitialState(),
  isThirdPartyVendorDiariesLoading: false,
  thirdPartyVendorDiaries: thirdPartyVendorDiaryAdapter.getInitialState(),
};

// Reducers
export const vendorManagementSlice = createSlice({
  name: 'vendorManagement',
  initialState: initialState,
  reducers: {
    setIsLoadingVendorList: (state: VendorManagementState, action: PayloadAction<boolean>) => {
      state.isLoadingVendorList = action.payload;
    },
    setVendorList: (state: VendorManagementState, action: PayloadAction<Vendor[]>) => {
      vendorListAdapter.setAll(state.vendorListHeaders, action.payload);
      state.vendorList = action.payload;
    },
    resetVendorList: (state: VendorManagementState) => {
      state.vendorListHeaders = initialState.vendorListHeaders;
      state.vendorList = initialState.vendorList;
      state.isLoadingVendorList = initialState.isLoadingVendorList;
    },
    setIsCreateOrUpdateVendorLoading: (state: VendorManagementState, action: PayloadAction<boolean>) => {
      state.isCreateOrUpdateVendorLoading = action.payload;
    },
    setIsLoadingVendor: (state: VendorManagementState, action: PayloadAction<boolean>) => {
      state.isLoadingVendor = action.payload;
    },
    setVendor: (state: VendorManagementState, action: PayloadAction<Vendor>) => {
      state.vendor = action.payload;
    },
    resetVendor: (state: VendorManagementState) => {
      state.vendor = initialState.vendor;
      state.isLoadingVendor = initialState.isLoadingVendor;
    },
    setIsDeleteVendorLoading: (state: VendorManagementState, action: PayloadAction<boolean>) => {
      state.isDeleteVendorLoading = action.payload;
    },
    setIsThirdPartyVendorDiariesLoading: (state, action: PayloadAction<boolean>) => {
      state.isThirdPartyVendorDiariesLoading = action.payload;
    },

    setThirdPartyVendorDiaries: (state, action: PayloadAction<ThirdPartyVendorDiary[]>) => {
      thirdPartyVendorDiaryAdapter.setAll(state.thirdPartyVendorDiaries, action.payload);
    },
  },
});

export const { selectAll: selectAllVendorListHeaders } = vendorListAdapter.getSelectors<RootState>(
  (state: RootState) => state.vendorManagement.vendorListHeaders
);

export const { selectAll: selectAllThirdPartyVendorDiaries } = thirdPartyVendorDiaryAdapter.getSelectors(
  (state: EntityState<ThirdPartyVendorDiary>) => state
);

const getThirdPartyVendorDiary = (state: RootState) => state.vendorManagement.thirdPartyVendorDiaries;
const getFilterText = (_s: RootState, filterText: string | undefined) => filterText;
export const selectThirdPartyVendorDiary = createSelector(
  [getThirdPartyVendorDiary, getFilterText],
  (diary, filterText) => {
    return filterObjects(selectAllThirdPartyVendorDiaries(diary), filterText || '', [
      'ActionTypeEnum',
      'ActionSummary',
      'ChangeByUser',
    ]);
  }
);
